/* eslint-disable no-console */
import {
  GEOLOCATION_PASSED,
  GeolocationPassed,
} from '@fanduel/geolocation-sdk';
import { useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { setExperiments } from '@/features/amplitude/store/amplitude';
import { WRAPPER_TO_GAME_CLIENT_EVENT_TYPES } from '@/features/event-handlers/wrapper-event-handlers/game-client-events/types';
import { markDetachedSessionResumed } from '@/features/game/detached-session-resume';
import { resetGameData, setGameData } from '@/features/game/store/game-data';
import { mapFgpResponseToGameData } from '@/features/game/store/thunks/helpers/map-fgp-response';
import { extractGameContextParams } from '@/features/game-context/extract-params';
import {
  getChannel,
  getPlatform,
} from '@/features/game-context/platform-helpers';
import { mapProvider } from '@/features/game-context/provider-mapper';
import {
  gameContextSelector,
  resetGameContext,
  setGameContext,
  VerifiedContextState,
} from '@/features/game-context/store/game-context';
import {
  HandshakePayload,
  LauncherEvent,
  LaunchParams,
  RGI,
} from '@/features/game-context/types';
import {
  geolocationPassed,
  resetGeolocationState,
} from '@/features/geolocation/store/geolocation';
import {
  ddNotifyClientInitialization,
  ddNotifyContextIntializedAction,
} from '@/features/logging/action-logger';
import { ddNotifyMissingParamsError } from '@/features/logging/error-logger';
import { Logger } from '@/features/logging/logger';
import { isWebWrapperAvailable } from '@/features/web-wrapper/web-wrapper-bridge';
import { WrapperProxy } from '@/features/wrapper-proxy/wrapper-proxy';

export const useGameHelpers = (
  triggerGeolocation: (force: boolean) => void,
) => {
  const heartbeat = useRef<NodeJS.Timeout | undefined>();
  const context = useSelector(gameContextSelector);
  const platform = getPlatform();
  const channel = getChannel();
  const dispatch = useDispatch();
  const { initialized, softInitialization } = useSelector(gameContextSelector);
  const initializeParams = useCallback(
    (payload: HandshakePayload) => {
      if (context.initialized) {
        return;
      }

      if (payload?.experiments) {
        dispatch(setExperiments(payload?.experiments));
      }

      const [launchParams, missingParamsList, geocomplyToken] =
        extractGameContextParams(payload.initializeGameParams);

      if (missingParamsList.length > 0) {
        WrapperProxy.setContextParamsMissing(missingParamsList.join(','));

        ddNotifyMissingParamsError(payload.initializeGameParams);

        return;
      }

      if (geocomplyToken) {
        dispatch(geolocationPassed(geocomplyToken));

        if (isWebWrapperAvailable()) {
          WrapperProxy.setGeolocationSuccess({
            type: GEOLOCATION_PASSED,
            payload: { jwt: geocomplyToken },
          } as GeolocationPassed);
        }
      }

      dispatch(
        setGameContext({
          initialized: true,
          platform,
          channel,
          ...launchParams,
          provider: mapProvider(launchParams as VerifiedContextState),
          ...(payload?.iosVersion && { iosVersion: payload?.iosVersion }),
        }),
      );

      Logger.initializeLoggerContext({
        experiments: payload?.experiments,
        version: payload?.iosVersion,
        provider: mapProvider(launchParams as VerifiedContextState),
        gameStudio: launchParams?.gameStudio,
        jackpotMode: launchParams?.jackpotMode,
        region: launchParams?.region?.toUpperCase(),
        channel,
      });
      WrapperProxy.setContextReceived();

      ddNotifyContextIntializedAction(launchParams as LaunchParams);
    },

    [context, platform, channel, dispatch],
  );

  const initializeGameClient = useCallback(() => {
    const iosVersion = new URL(window.location.href).searchParams.get(
      'version',
    );

    ddNotifyClientInitialization();

    // Ask the launcher for the current game window UI state so provider
    // listeners stay unregistered until the launcher confirms it is enabled.
    WrapperProxy.requestGameWindowUIState();

    if (!iosVersion || softInitialization) {
      console.log(
        `[V3_IOS]: No iOS version: ${iosVersion} or softInitialization: ${softInitialization} -> setting initialized`,
      );
      WrapperProxy.setGameClientInitialized();
      return;
    }

    console.log(iosVersion, '[V3_IOS]: iosVersion -> Process version');
    if (!iosVersion) {
      WrapperProxy.setGameClientInitialized();
      return;
    }

    if (
      iosVersion &&
      iosVersion === '3.0' &&
      !initialized &&
      !softInitialization
    ) {
      console.log('[V3_IOS]: iosVersion -> Starting heartbeat');
      heartbeat.current = setInterval(() => {
        WrapperProxy.setGameClientInitialized();
      }, 100);
      return;
    }
    console.log('[V3_IOS]: Clearing heartbeat');

    if (heartbeat?.current) clearInterval(heartbeat.current);
  }, [initialized, softInitialization]);

  const initializeGameURL = useCallback(
    (event) => {
      console.log(
        '%c [IOS_GLS_USE_PRISMIC]: Initialized Game URL',
        'color:rgb(255, 255, 0)',
      );
      if (
        !event.data?.payload ||
        event.data?.type !==
          WRAPPER_TO_GAME_CLIENT_EVENT_TYPES.INITIALIZE_URL ||
        !event.data.payload.data.gameData.provider ||
        context.initialized
      ) {
        return;
      }

      // Ask the launcher for the current game window UI state so provider
      // listeners stay unregistered until the launcher confirms it is enabled.
      WrapperProxy.requestGameWindowUIState();

      if (event.data.payload?.experiments) {
        dispatch(setExperiments(event.data.payload?.experiments));
      }

      const gameData = mapFgpResponseToGameData(
        event.data.payload,
        event.data.payload.data.gameData.provider.toUpperCase(),
      );
      if (gameData) {
        dispatch(setGameData(gameData));
      }
      const gameCtx = {
        jackpotMode: event.data.payload?.data?.gameData?.jackpotMode,
        provider: event.data.payload?.data?.gameData?.provider.toUpperCase(),
        providerRef: event.data.payload?.data?.gameData?.providerRef,
        gameName: event.data.payload?.data?.gameData?.gameName,
        gameStudio: event.data.payload?.data?.gameData?.gameStudio,
        region: event.data.payload?.data?.gameData?.region?.toUpperCase(),
        brand: event.data.payload?.brand,
        userId: event.data.payload?.userId,
        rgi: RGI.FGP,
        channel,
        initialized: true,
        softInitialization: false,
        ...(event.data.payload?.iosVersion && {
          iosVersion: event.data.payload?.iosVersion,
        }),
      };
      dispatch(setGameContext(gameCtx));
      Logger.initializeLoggerContext(gameCtx);
      WrapperProxy.setContextReceived();
    },
    [context.initialized, channel, dispatch],
  );

  const initializeClientHandler = useCallback(
    (event: MessageEvent<LauncherEvent<HandshakePayload>>) => {
      if (
        event.origin &&
        event.data.type === WRAPPER_TO_GAME_CLIENT_EVENT_TYPES.INITIALIZE_PARAMS
      ) {
        initializeParams(event.data.payload);
      }
      if (
        event.origin &&
        event.data.type ===
          WRAPPER_TO_GAME_CLIENT_EVENT_TYPES.TRIGGER_GEOLOCATION
      ) {
        triggerGeolocation(true);
      }
    },
    [initializeParams, triggerGeolocation],
  );
  const iosSoftReload = useCallback(
    (event: MessageEvent) => {
      if (
        event.data.type === WRAPPER_TO_GAME_CLIENT_EVENT_TYPES.IOS_SOFT_RELOAD
      ) {
        if (initialized) {
          dispatch(resetGameContext({ softInitialization: true }));
          dispatch(resetGeolocationState());
          dispatch(resetGameData());
        }
      }
    },
    [initialized, dispatch],
  );

  const resumeDetachedSession = useCallback(
    (event: MessageEvent) => {
      const payload = event.data?.payload;
      if (!payload) {
        return;
      }

      const { handshakeParams } = payload;
      const snapshot = payload.detachedSnapshot;

      if (handshakeParams) {
        initializeParams(handshakeParams);
      }

      if (snapshot?.geocomplyToken) {
        dispatch(geolocationPassed(snapshot.geocomplyToken));
      }

      if (snapshot?.fgpGameData) {
        dispatch(setGameData(snapshot.fgpGameData));
        markDetachedSessionResumed();
        WrapperProxy.setFetchGameSuccess();
        WrapperProxy.setGameLoaded();
      }
    },
    [dispatch, initializeParams],
  );

  return {
    initializeGameClient,
    initializeClientHandler,
    iosSoftReload,
    initializeGameURL,
    resumeDetachedSession,
  };
};
